<template>
  <el-button
    class="f360-button"
    :class="{ ...wrapperClass }"
    :disabled="disabled"
    v-bind="$attrs"
    :loading="loading"
  >
    <span
      class="f360-button__label"
      :class="{ 'f360-button__label--small': size === 'small' }"
    >
      <MaterialIcon
        :icon="prefixIcon"
        class="f360-button__icon"
        :style="{ color: iconCustomColor }"
        v-if="prefixIcon && !loading"
      />
      <slot name="default" />
      <MaterialIcon
        :icon="suffixIcon"
        class="f360-button__icon"
        v-if="suffixIcon && !loading"
        :style="{ color: iconCustomColor }"
      />
    </span>
  </el-button>
</template>

<script setup lang="ts">
import { computed } from "vue";
import MaterialIcon from "@/ui/components/Icons/MaterialIcon/MaterialIcon.vue";

const props = withDefaults(
  defineProps<{
    prefixIcon?: string;
    suffixIcon?: string;
    disabled?: boolean;
    outline?: boolean;
    link?: boolean;
    filled?: boolean;
    full?: boolean;
    size?: "small" | "default" | "large";
    base?: "default" | "neutral";
    iconSize?: string;
    loading?: boolean;
    iconCustomColor?: string;
    customType?: "danger-full" | "default";
  }>(),
  {
    size: "default",
    type: "default",
    customType: "default"
  }
);

const wrapperClass = computed(() => ({
  "f360-button--outline": props.outline,
  "f360-button--link": props.link,
  "f360-button--disabled": props.disabled,
  "f360-button--filled": props.filled,
  "f360-button--full": props.full,
  "f360-button--small": props.size === "small",
  "f360-button--neutral": props.base === "neutral",
  "f360-button--danger-full": props.customType === "danger-full"
}));
</script>

<style scoped lang="scss">
@use "@/ui/styles/variables" as *;
@use "@/ui/styles/theme/f360-theme" as *;

.f360-button {
  min-height: 32px;
  width: fit-content;
  padding: 8px;
  border-radius: 5px;
  border: 1px solid $button-border-f360;
  color: $font-color-f360;

  &__label {
    display: flex;
    align-items: center;
    gap: 8px;

    @include font-schema(14px, 500, 16px);

    &--small {
      @include font-schema(12px, 500, 12px);
    }
  }

  &__icon {
    font-size: v-bind(iconSize);
  }

  &.financas-button--danger {
    color: #ed1517;
    background-color: #fff1f1;
    border-color: #ed1517;

    &:hover {
      background-color: #ed1517;
      color: #fff1f1;
    }
  }

  &--danger-full {
    background-color: _map.get($vermelho, 700);
    color: #fff;
    border: 1px solid _map.get($vermelho, 700);

    &:hover {
      background-color: _map.get($vermelho, 800);
      color: #fff;
      border: 1px solid _map.get($vermelho, 800);
    }

    &:disabled {
      background-color: _map.get($vermelho, 500);
      color: #fff;
      border: 1px solid _map.get($vermelho, 500);
    }
  }

  // Tamanho
  &--full {
    width: 100%;
  }

  &--small {
    height: 24px;
    min-height: 24px;
  }

  // Estados
  &--outline {
    background: transparent;
    border: none;
    color: $button-color-active;

    &:hover {
      background: $button-outline-hover;
    }
  }

  &--link {
    background: transparent;
    border: none;
    color: $button-color-active;

    &:hover {
      background: $button-outline-hover;
    }
  }

  &--filled {
    background: $button-filled-background;
    border: none;
    color: #fff;

    &:hover {
      background: $button-filled-hover;
    }
  }

  &--neutral {
    border: 1px solid #e7e7e7;
    color: #5d5d5d;

    &:hover {
      background: #f6f6f6;
    }
  }

  &--disabled {
    background-color: #d1d1d1 !important;
    color: $button-color-disable;
    border: #d1d1d1 !important;

    &:hover {
      color: $button-color-disable;
    }

    &.f360-button {
      &--outline {
        background: transparent !important;
        border: none !important;
      }
    }
  }
}
</style>
